<template>
  <PopupDialog
    :open="open"
    required
    z-class="z-40"
    content-class="bg-base-100 max-w-max"
    ignore-pos-dialog
  >
    <form @submit.prevent="submit" class="grid w-full max-w-sm gap-4 p-5">
      <fieldset class="fieldset">
        <div class="flex items-end justify-between">
          <SourceInfo :source="currentSource" class="mt-2" />
          <div class="flex flex-col items-end">
            <a class="label link" @click.prevent="turnOffPos">
              {{ $t('pos.turn-off-pos') }}
            </a>
            <legend class="label">{{ $t('pos.pin-login-title') }}</legend>
          </div>
        </div>

        <PinPadInput
          ref="pinPad"
          v-model="pin"
          class="mt-4"
          :disabled="isPinLoginPending"
          :error="loginError"
          autofocus
          @complete="submit"
        />

        <button class="btn btn-primary btn-lg mt-5" type="submit" :disabled="!canSubmit">
          {{ $t('global.login') }}
        </button>
      </fieldset>
    </form>
  </PopupDialog>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import { PIN_MIN_LENGTH } from '../../constants';
import { useAuth } from '../../stores/authStore';
import { useLoading } from '../../stores/loadingStore';
import { usePos } from '../../stores/posStore';
import { useSource } from '../../stores/sourceStore';
import PopupDialog from '../dialog/PopupDialog.vue';
import SourceInfo from '../source/SourceInfo.vue';
import PinPadInput from './PinPadInput.vue';

const props = withDefaults(
  defineProps<{
    hideOnRoutes?: string[];
  }>(),
  {
    hideOnRoutes: () => ['login', 'pinLogin'],
  },
);

const route = useRoute();
const { loading, watchLoading } = useLoading();
const { pinLogin, isPinLoginPending, showPinDialog, loginError } = useAuth();
const { exitPosMode } = usePos();
const { currentSource } = useSource();

const pinPad = useTemplateRef<InstanceType<typeof PinPadInput>>('pinPad');

const pin = ref<number[]>([]);

const open = computed(
  () => showPinDialog.value && !props.hideOnRoutes?.includes(route.name as string),
);

const canSubmit = computed(() => !loading.value && pin.value.length >= PIN_MIN_LENGTH);

// The auth store's POS end handler signs out and routes to the password form.
const turnOffPos = () => exitPosMode();

const submit = () => {
  if (!canSubmit.value) return;

  pinLogin(pin.value.join('')).catch(() => {
    // Errors surface through `loginError`; wipe the entry so the next attempt
    // starts from an empty pad.
    pinPad.value?.reset();
  });
};

watch(open, (open, prevOpen) => {
  if (open && !prevOpen) pinPad.value?.reset();
});

watchLoading(isPinLoginPending);
</script>
